import { forwardRef } from 'react'
import { ThinkingOrb } from 'thinking-orbs'

/* ─────────────────────────────────────────────────────────────
   Monoline UI — yuklanish belgilari

   Oltita holat, har biri boshqa vazifa uchun. Nima uchun bittasi
   yetmaydi: "sahifa ochilmoqda" va "AI javob o'ylayapti" — bir xil
   kutish emas. Belgining shakli operatorga NIMA kutilayotganini aytadi,
   ya'ni u ham ma'lumot tashiydi — rang bilan bir xil qoidada.

   Chizishni `thinking-orbs` (MIT, Jakub Antalik) bajaradi: canvas 2D,
   qo'lda sozlangan oltita animatsiya. Tizimga to'g'ri keladi, chunki
   u qurilishi bo'yicha kulrang — butun palitrasi `rgba(a,a,a,o)`,
   uch kanal ham bir xil. Ya'ni monoxrom tizimga rang olib kirmaydi.

   Mavzuni o'zi aniqlaydi: `data-theme` atributini ota-elementlarda
   kuzatadi (bizda uni index.html React'dan oldin qo'yadi), bo'lmasa
   tizim sozlamasiga qaraydi. Ekrandan chiqsa yoki tab yashirilsa
   animatsiyani to'xtatadi va harakat kamaytirish rejimini hurmat qiladi.

   DIQQAT — bog'liqlik: kitning qolgan komponentlari tashqi kutubxonasiz
   ishlaydi, faqat SHU fayl (`Loader` va `Beacon`) `thinking-orbs` ga
   tayanadi. Kitni boshqa loyihaga ko'chirsangiz, uni ham o'rnatish kerak:
       npm install thinking-orbs
   ───────────────────────────────────────────────────────────── */

function cx(...a) { return a.filter(Boolean).join(' ') }

/**
 * Kitning uch o'lchami -> paketning ikki o'lchami.
 * `thinking-orbs` da 20 va 64 — masshtab emas, IKKI ALOHIDA dizayn:
 * har birining nuqta soni, nuqta kattaligi va tezligi alohida sozlangan.
 * Shuning uchun oraliq qiymat yo'q: `sm` va `md` ham 20 ni oladi
 * (matn yonidagi ko'rinish), `lg` esa 64 ni (sahifa markazi).
 */
const ORB_SIZE = { sm: 20, md: 20, lg: 64 }

/** Holat -> standart matn. Ekranda nima kutilayotgani yozilib turadi. */
const DEFAULT_LABEL = {
  working: 'Yuklanmoqda',
  searching: 'Qidirilmoqda',
  solving: 'Tahlil qilinmoqda',
  listening: 'Tinglanmoqda',
  composing: 'Yuborilmoqda',
  shaping: 'Xarita tayyorlanmoqda',
}

/**
 * Yuklanish belgisi.
 *
 * @param state   working | searching | solving | listening | composing | shaping
 * @param size    sm | md (20px) | lg (44+ -> 64px)
 * @param label   yonida chiqadigan matn. `false` bersangiz matn chiqmaydi,
 *                lekin ekran o'quvchi uchun aria-label baribir qoladi.
 * @param block   sahifa o'rtasida, matn ostida ko'rsatiladigan ko'rinish
 * @param speed   tezlik ko'paytirgichi (paketning o'z sozlamasi ustiga)
 * @param paused  animatsiyani joriy kadrda to'xtatish
 */
export const Loader = forwardRef(function Loader(
  { state = 'working', size = 'md', label, block = false, speed, paused,
    className, ...rest },
  ref
) {
  const st = DEFAULT_LABEL[state] ? state : 'working'
  const text = label === undefined ? DEFAULT_LABEL[st] : label

  return (
    <span
      ref={ref}
      className={cx(
        'ui-loader',
        size !== 'md' && `ui-loader--${size}`,
        block && 'ui-loader--block',
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={text || DEFAULT_LABEL[st]}
      {...rest}
    >
      <ThinkingOrb
        state={st}
        size={ORB_SIZE[size] || ORB_SIZE.md}
        theme="auto"
        speed={speed}
        paused={paused}
        aria-hidden="true"
        className="ui-loader__orb"
      />
      {text && <span className="ui-loader__lbl">{text}</span>}
    </span>
  )
})
Loader.displayName = 'Loader'

/**
 * Beacon — diqqat belgisi: `listening` orbi.
 *
 * Muammo joyini faqat rang emas, shakl va harakat bilan belgilaydi
 * (DESIGN.md 8). Yuklanish belgisi bilan bir xil shakl — bu ataylab
 * qilingan: orb "bu yerga qara" degan yagona harakat, ma'noni esa yonidagi
 * matn beradi.
 *
 * `Loader` dan farqi — vazifasi: bu jarayon emas, HOLAT belgisi. Shuning
 * uchun `role="status"` va `aria-live` yo'q: ro'yxatdagi har qatorda
 * "Qidirilmoqda" deb takrorlanmaydi, oddiy rasm sifatida o'qiladi.
 *
 * @param size   'sm' | 'md' (20px) | 'lg' (64px). Raqam ham beriladi —
 *               44 dan boshlab katta o'lcham tanlanadi (ikkalasi paketning
 *               ALOHIDA dizayni, oraliq qiymat yo'q).
 * @param tone   critical (standart) | inherit — `inherit` da filtr qo'yilmaydi
 * @param label  ekran o'quvchi uchun matn. Yonida shu ma'nodagi matn bo'lsa
 *               `label={false}` bering — belgi bezakka aylanadi.
 */
export const Beacon = forwardRef(function Beacon(
  { size = 'sm', tone = 'critical', label = 'Diqqat talab qilinadi',
    className, ...rest },
  ref
) {
  const px = typeof size === 'number'
    ? (size >= 44 ? 64 : 20)
    : (size === 'lg' ? 64 : 20)
  const hidden = label === false || label == null

  return (
    <span
      ref={ref}
      className={cx('ui-beacon', tone !== 'inherit' && `ui-beacon--${tone}`, className)}
      role={hidden ? undefined : 'img'}
      aria-label={hidden ? undefined : label}
      aria-hidden={hidden || undefined}
      title={hidden ? undefined : label}
      {...rest}
    >
      <ThinkingOrb
        state="listening"
        size={px}
        theme="auto"
        aria-hidden="true"
        className="ui-beacon__orb"
      />
    </span>
  )
})
Beacon.displayName = 'Beacon'
